import React, { useEffect, useRef, useState } from "react";
import AppTable from "../../common/AppTable/AppTable";
import { useDispatch, useSelector } from "react-redux";
import {
  createCategory,
  deleteCategory,
  exportCSV,
  fetchAllCategories,
  updateCategory,
  uploadCSV,
} from "../../redux/categorySlice";
import AddBtn from "../../common/AppSpecificConpnents/AddBtn";
import UploadViaCsvBtn from "../../common/AppSpecificConpnents/UploadViaCsvBtn";
import ExportCsvBtn from "../../common/AppSpecificConpnents/ExportCsvBtn";
import FilterDropdown from "../../common/AppSpecificConpnents/PartsTable/FilterDropdown";
import FieldNameDropdown from "./FieldNameDropdown";
import { scrollToTop } from "../../util/AppUtils";
import { showSnackbar } from "../../redux/snackbarSlice";
import CatgryData from "./CatgryData";
import { GetAuditTrailForImport } from "../../util/getAuditTrailData";
import DeleteConfirmation from "../../common/Confirmations/DeleteConfirmation";
import ImportCsvSuccessPopup from "../../common/ImportCsvSuccessPopup";
import Loader from "../../common/Loader";
import { TableActionGroup } from "../../common/Buttons";

const CategoryTable = ({
  handleSelectCategory,
  disableFilter = false,
  cat_type = "all",
  addRow,
  useTableInAuditTrail = false,
  auditTrailRow,
}) => {
  const columns = [
    {
      headName: "Field Name",
      field: "field_name",
      sort: true,
      filter: true,
      required: true,
      width: "30%",
    },
    {
      headName: "Category Name",
      field: "category_name",
      sort: true,
      filter: true,
      required: true,
      width: "30%",
    },
    {
      headName: "Description",
      field: "description",
      sort: true,
      filter: true,
      width: "30%",
    },
    { headName: "Action", field: "action", textCenter: true, isAction: true },
  ];
  const [showLoader, setShowLoader] = useState(false);
  const tableContainerRef = useRef();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [importedInfo, setImportData] = useState(null);
  const { categories, pagination } = useSelector((state) => state.category);
  const dispatch = useDispatch();

  const snackbar = (message, type) => {
    dispatch(showSnackbar({ type: type, message: message }));
  };

  const [filterType, setFilterType] = useState(cat_type);

  useEffect(() => {
    if (!useTableInAuditTrail)
      if (searchQuery.length) {
        let query = searchQuery;
        if (filterType !== "all" && filterType !== undefined) {
          query += `&field_name=${filterType}`;
        }
        localStorage.categoryTableSearchQuery = query;
        dispatch(fetchAllCategories({ searchQuery: query }));
      }
  }, [dispatch, searchQuery, filterType, useTableInAuditTrail]);

  // Fetch when pagination (offset/limit) or filter changes so page changes trigger API
  useEffect(() => {
    if (useTableInAuditTrail) return;
    const query =
      `limit=${limit}&offset=${offset}` +
      (filterType && filterType !== "all" && filterType !== undefined
        ? `&field_name=${encodeURIComponent(filterType)}`
        : "");
    dispatch(fetchAllCategories({ searchQuery: query }));
  }, [offset, limit, filterType, useTableInAuditTrail, dispatch]);

  const [newRow, setNewRow] = useState(null);
  const [editRow, setEditedRow] = useState(null);
  const handleInputChange = (e) => {
    setNewRow((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleInputChangeEdit = (e) => {
    setEditedRow((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handleAddRow = async () => {
    if (
      newRow.field_name.length === 0 ||
      newRow.category_name.trim().length === 0
    ) {
      snackbar("Field Name, Category Name are required!", "error");
      return;
    }

    const categoryData = {
      field_name: newRow.field_name.trim(),
      category_name: newRow.category_name.trim(),
      description: newRow.description ? newRow.description.trim() : "",
    };

    try {
      setShowLoader(true);
      const resp = await dispatch(createCategory(categoryData));
      setShowLoader(false);
      snackbar(
        resp.payload.message,
        resp.payload.success ? "success" : "error"
      );
      if (!resp.payload.success) {
        return;
      }
      dispatch(
        fetchAllCategories({
          searchQuery: localStorage.categoryTableSearchQuery,
        })
      );
      setNewRow(null);
    } catch (error) {
      snackbar("Category creation failed! " + error?.message, "error");
    }
  };
  const updateRow = async () => {
    if (
      editRow.field_name.length === 0 ||
      editRow.category_name.trim().length === 0
    ) {
      snackbar("Field Name, Category Name are required!", "error");
      return;
    }

    setShowLoader(true);
    const response = await dispatch(
      updateCategory({
        id: editRow.category_id,
        data: { ...editRow, category_name: editRow.category_name?.trim() },
        parent_version: editRow.version_id_ref,
      })
    );
    setShowLoader(false);
    dispatch(
      fetchAllCategories({ searchQuery: localStorage.categoryTableSearchQuery })
    );
    if(response.payload.success){
      snackbar("Category updated successfully!", "success");
      setEditedRow(null);
    } else {
      snackbar(response.payload.message, "error");
    }
  };
  const fileInputRef = useRef(null);
  const handleFileChange = async (e, actionType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset the file input to allow same file upload next time
    e.target.value = "";

    const auditTrailData = await GetAuditTrailForImport();

    try {
      setShowLoader(true);
      const response = await dispatch(
        uploadCSV({ file: file, auditTrailData })
      );
      setShowLoader(false);
      if(response?.payload?.success){
        snackbar(
          response.payload.message,
          "success"
        );
      }
      
      if (response?.payload?.success) {
        dispatch(
          fetchAllCategories({
            searchQuery: localStorage.categoryTableSearchQuery,
          })
        );
      }
      setImportData(response?.payload);
    } catch (error) {
      snackbar(
        "Error while importing CSV: " + (error.message || "Unknown error"),
        "error"
      );
    }
  };
  const handleExportCSV = () => {
    setShowLoader(true);
    dispatch(exportCSV()).then((res) => {
      setShowLoader(false);
      if (exportCSV.fulfilled.match(res)) {
        snackbar("Categories exported successfully.", "success");
      } else {
        snackbar("Error exporting CSV: " + res.payload, "error");
      }
    });
  };
  const handleDelete = async (id) => {
    DeleteConfirmation(() => {
      setShowLoader(true);
      dispatch(deleteCategory(id)).then((resp) => {
        setShowLoader(false);
        snackbar("Category deleted successfully!", "success");
        dispatch(
          fetchAllCategories({
            searchQuery: localStorage.categoryTableSearchQuery,
          })
        );
      });
    }, "Are you sure you want to delete this category?");
  };

  return (
    <>
      <AppTable
        ref={tableContainerRef}
        columnsData={
          useTableInAuditTrail
            ? columns
                .map((d) => ({ ...d, sort: false }))
                .filter((a) => a.field !== "action")
            : columns
        }
        showSearchInput={useTableInAuditTrail ? false : true}
        contentBeforeSearchFilter={
          <>
            <FilterDropdown
              filterOptions={CatgryData}
              filterType={filterType}
              setFilterType={setFilterType}
              disableFilter={disableFilter}
            />
          </>
        }
        paginate={useTableInAuditTrail ? false : true}
        pagination={{
          limit: [limit, setLimit],
          offset: [offset, setOffset],
          pagination,
          maxVsbleButtons: 7,
          paginationPosition: {
            position: "center",
            leftContent: (
              <>
                {addRow && (
                  <AddBtn
                    onClick={() => {
                      // Check if a row is already being edited
                      if (editRow !== null) {
                        snackbar(
                          "Please save or cancel the current row before adding or editing another one.",
                          "error"
                        );
                        return;
                      }

                      // Check if a new row is already being added
                      if (newRow !== null) {
                        snackbar(
                          "Please save or cancel the current row before adding or editing another one.",
                          "error"
                        );
                        return;
                      }

                      scrollToTop(tableContainerRef);
                      setNewRow({
                        field_name: "",
                        category_name: "",
                        description: "",
                      });
                    }}
                  />
                )}
              </>
            ),
            rightContent: (
              <>
                <UploadViaCsvBtn
                  fileInputRef={fileInputRef}
                  handleFileChange={handleFileChange}
                />{" "}
                &nbsp;
                <ExportCsvBtn handleExportCSV={handleExportCSV} />
              </>
            ),
          },
          paginationShadow: true,
        }}
        usingInPopup={true}
        onFilterSortHandler={setSearchQuery}
      >
        {!useTableInAuditTrail && (
          <>
            {newRow && (
              <tr>
                <td>
                  <FieldNameDropdown
                    defaultValue={newRow.field_name}
                    onChange={setNewRow}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    style={{ height: "25px", fontSize: "13px", width: "100%" }}
                    name="category_name"
                    value={newRow.category_name}
                    onChange={handleInputChange}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    style={{ height: "25px", fontSize: "13px", width: "100%" }}
                    name="description"
                    value={newRow.description}
                    onChange={handleInputChange}
                  />
                </td>
                <td>
                  <TableActionGroup
                    mode="editing"
                    onSave={handleAddRow}
                    onCancel={() => setNewRow(null)}
                  />
                </td>
              </tr>
            )}

            {categories.map((row, i) => {
              let isEditing = false;
              let isUsedSomeWhere =
                row.used_in_bom_table_for_step_title ||
                row.used_in_canvas_template_column_title ||
                row.used_in_part_number_column_title ||
                row.used_in_rich_text_category
                  ? true
                  : false;
              if (editRow !== null && row.category_id === editRow?.category_id)
                isEditing = true;
              return (
                <tr key={row.category_id + "__" + i}>
                  <td>
                    {isEditing ? (
                      <FieldNameDropdown
                        defaultValue={{
                          value: editRow?.field_name,
                          label: editRow?.field_name,
                        }}
                        onChange={setEditedRow}
                        isDisabled={false}
                      />
                    ) : (
                      row.field_name
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        style={{
                          height: "25px",
                          fontSize: "13px",
                          width: "100%",
                        }}
                        name="category_name"
                        defaultValue={editRow?.category_name}
                        onChange={handleInputChangeEdit}
                      />
                    ) : (
                      row.category_name
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="text"
                        style={{
                          height: "25px",
                          fontSize: "13px",
                          width: "100%",
                        }}
                        name="description"
                        defaultValue={editRow?.description}
                        onChange={handleInputChangeEdit}
                      />
                    ) : (
                      row.description
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <TableActionGroup
                        mode="editing"
                        onSave={updateRow}
                        onCancel={() => setEditedRow(null)}
                      />
                    ) : (
                      <TableActionGroup
                        mode="view"
                        showEdit={true}
                        showDelete={!handleSelectCategory}
                        showSelect={!!handleSelectCategory}
                        disableEdit={editRow !== null || newRow !== null}
                        disableDelete={
                          isUsedSomeWhere || editRow !== null || newRow !== null
                        }
                        disableSelect={editRow !== null || newRow !== null}
                        onEdit={() => {
                          // Check if a row is already being edited
                          if (editRow !== null) {
                            snackbar(
                              "Please save or cancel the current row before adding or editing another one.",
                              "error"
                            );
                            return;
                          }

                          // Check if a new row is already being added
                          if (newRow !== null) {
                            snackbar(
                              "Please save or cancel the current row before adding or editing another one.",
                              "error"
                            );
                            return;
                          }
                          setEditedRow(row);
                        }}
                        onDelete={() => {
                          // Check if a row is already being edited
                          if (editRow !== null) {
                            snackbar(
                              "Please save or cancel the current row before adding or editing another one.",
                              "error"
                            );
                            return;
                          }

                          // Check if a new row is already being added
                          if (newRow !== null) {
                            snackbar(
                              "Please save or cancel the current row before adding or editing another one.",
                              "error"
                            );
                            return;
                          }
                          handleDelete(row.category_id);
                        }}
                        onSelect={() => handleSelectCategory(row)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </>
        )}
        {useTableInAuditTrail && <>{auditTrailRow}</>}
      </AppTable>

      {importedInfo && (
        <ImportCsvSuccessPopup
          importedInfo={importedInfo}
          setNullInportData={setImportData}
        />
      )}

      {showLoader && <Loader />}
    </>
  );
};

export default CategoryTable;
